<template>
  <div ref="rootEl" class="maplibregl-ctrl maplibregl-ctrl-group BottomRightMapControls">
    <ResetMapExtentControl inline />
    <MapToolsControl inline />
    <DragDropGeodataControl inline />
    <MyMapExportButton inline />
    <MyMapLegend inline />
  </div>
</template>

<script setup lang="ts">
import DragDropGeodataControl from '@/components/map/controls/DragDropGeodataControl.vue'
import MapToolsControl from '@/components/map/controls/MapToolsControl.vue'
import ResetMapExtentControl from '@/components/map/controls/ResetMapExtentControl.vue'
import MyMapExportButton from '@/views/map/components/export/MyMapExportButton.vue'
import MyMapLegend from '@/views/map/components/MyMapLegend.vue'
import { useMyMapStore } from '@/stores/myMapStore'
import maplibregl from 'maplibre-gl'
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'

const myMapStore = useMyMapStore()
const rootEl = ref<HTMLElement | null>(null)
let navControl: maplibregl.NavigationControl | null = null
// Created programmatically to keep it invisible to Vue's virtual DOM and prevent
// insertBefore conflicts when MapLibre injects its own DOM nodes.
let navContainerEl: HTMLElement | null = null

function setupNavControl(map: maplibregl.Map): void {
  if (navControl || !navContainerEl) return
  navControl = new maplibregl.NavigationControl({ showCompass: false })
  const navEl = navControl.onAdd(map)
  navContainerEl.appendChild(navEl)
}

onMounted(() => {
  navContainerEl = document.createElement('div')
  navContainerEl.className = 'BottomRightMapControls__nav'
  rootEl.value?.prepend(navContainerEl)

  const existingMap = myMapStore.myMap?.map
  if (existingMap) setupNavControl(existingMap)
})

watch(
  () => myMapStore.myMap?.map,
  (map) => {
    if (!map) return
    setupNavControl(map)
  },
)

onBeforeUnmount(() => {
  if (navControl) {
    navControl.onRemove()
    navControl = null
  }
  navContainerEl?.remove()
  navContainerEl = null
})
</script>

<style lang="scss" scoped>
.BottomRightMapControls {
  --bottom-right-controls-w: 3.25rem;
  width: var(--bottom-right-controls-w);
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  gap: 0.25rem;
}

.BottomRightMapControls__nav {
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  gap: 0.25rem;
  width: 100%;
}

.BottomRightMapControls__nav :deep(.maplibregl-ctrl-group) {
  margin: 0;
}

.BottomRightMapControls__nav :deep(.maplibregl-ctrl) {
  margin: 0;
}

</style>
